import {
    Box,
    Button,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Typography,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    Checkbox,
    CircularProgress,
    TextField
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useMemo, useRef, useState } from 'react';
import SectionFormBox from '../../../../components/SectionFormBox/SectionFormBox';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { TipoObraItem, SubtipoObraItem } from '../../consts/avisoObraTypes';
import { useGetTiposActividadesEspecialesQuery } from '../../../../services/avisoObraApi';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
interface StepTwoProps {
    t: any;
    formData: any;
    handleChange: (event: { target: { name: string; value: any } }) => void;
    tiposObra?: TipoObraItem[];
    isView?: boolean;
    errors?: Record<string, string>;
}

interface TipoObraSeleccionado {
    tipoId: number;
    tipoNombre: string;
    subtipos: { id: number; descripcion: string }[];
}

interface ActividadEspecialSeleccionada {
    id: number;
    fechaInicio: Dayjs | null;
    fechaFin: Dayjs | null;
}

const StepTwo = ({ t, tiposObra, handleChange, formData, isView, errors = {} }: StepTwoProps) => {
    const [addTipoObra, setAddTipoObra] = useState(false);
    const prevActividadesRef = useRef<string>('');
    const [tipoSeleccionadoId, setTipoSeleccionadoId] = useState<number | ''>('');
    const [subtiposSeleccionadosIds, setSubtiposSeleccionadosIds] = useState<number[]>([]);
    const [selectedTiposObra, setSelectedTiposObra] = useState<TipoObraSeleccionado[]>([]);
    const hasHydratedTipos = useRef(false);
    const prevTiposRef = useRef<string>('');
    const [descripcionLocal, setDescripcionLocal] = useState('');

    const fechaInicioObra = useMemo(() => dayjs(formData.fechaInicio), [formData.fechaInicio]);
    const fechaFinObra = useMemo(() => dayjs(formData.fechaFin), [formData.fechaFin]);

    const {
        data: actividadesEspeciales,
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    } = useGetTiposActividadesEspecialesQuery();

    useEffect(() => {
        if (formData.avisosObraActividadesEspeciales) {
            const inicial = formData.avisosObraActividadesEspeciales.map((item: any) => {
                const parseFecha = (valor: string | null) => {
                    if (!valor) return null;
                    return valor.includes('T') ? dayjs.utc(valor) : dayjs(valor, 'DD/MM/YYYY');
                };

                return {
                    id: item.idActividad,
                    fechaInicio: parseFecha(item.fechaInicio),
                    fechaFin: parseFecha(item.fechaFin)
                };
            });

            setActividadesSeleccionadas(inicial);
        }
    }, []);

    useEffect(() => {
        if (
            !hasHydratedTipos.current &&
            tiposObra &&
            tiposObra.length > 0 &&
            formData.avisosObraTiposSubtipos
        ) {
            const rehidratados = formData.avisosObraTiposSubtipos
                .map((item: any) => {
                    const tipo = tiposObra.find((t) => t.id === item.idTipoObra);
                    if (!tipo) return null;

                    const subtipos = tipo.subtipos.filter((s) =>
                        item.idSubtiposObra.includes(s.id)
                    );

                    return {
                        tipoId: tipo.id,
                        tipoNombre: tipo.nombre,
                        subtipos: subtipos.map((s) => ({
                            id: s.id,
                            descripcion: s.descripcion
                        }))
                    };
                })
                .filter(Boolean);

            setSelectedTiposObra(rehidratados);
            hasHydratedTipos.current = true;
        }
    }, [tiposObra, formData.avisosObraTiposSubtipos]);

    const loadingActividades = isLoading || isFetching;

    const actividadesExcavacion = useMemo(
        () => (actividadesEspeciales || []).filter((a) => a.actividad === false),
        [actividadesEspeciales]
    );
    const actividadesGenerales = useMemo(
        () => (actividadesEspeciales || []).filter((a) => a.actividad === true),
        [actividadesEspeciales]
    );

    const [actividadesSeleccionadas, setActividadesSeleccionadas] = useState<
        ActividadEspecialSeleccionada[]
    >([]);

    const toggleActividad = (id: number) => {
        setActividadesSeleccionadas((prev) => {
            const existente = prev.find((a) => a.id === id);
            if (existente) {
                return prev.filter((a) => a.id !== id);
            } else {
                return [...prev, { id, fechaInicio: null, fechaFin: null }];
            }
        });
    };

    const setFechaActividad = (
        id: number,
        campo: 'fechaInicio' | 'fechaFin',
        value: Dayjs | null
    ) => {
        setActividadesSeleccionadas((prev) =>
            prev.map((a) => (a.id === id ? { ...a, [campo]: value } : a))
        );
    };

    const subtiposDelTipoSeleccionado: SubtipoObraItem[] = useMemo(() => {
        const tipo = tiposObra?.find((t) => t.id === tipoSeleccionadoId);
        return tipo?.subtipos ?? [];
    }, [tiposObra, tipoSeleccionadoId]);

    const resetCreacion = () => {
        setAddTipoObra(false);
        setTipoSeleccionadoId('');
        setSubtiposSeleccionadosIds([]);
    };

    const handleAddTipoObra = () => {
        if (!tipoSeleccionadoId || subtiposSeleccionadosIds.length === 0) return;

        const tipo = tiposObra?.find((t) => t.id === tipoSeleccionadoId);
        if (!tipo) return;

        const subtiposElegidos = tipo.subtipos.filter((s) =>
            subtiposSeleccionadosIds.includes(s.id)
        );
        setSelectedTiposObra((prev) => [
            ...prev,
            {
                tipoId: tipo.id,
                tipoNombre: tipo.nombre,
                subtipos: subtiposElegidos.map((s) => ({ id: s.id, descripcion: s.descripcion }))
            }
        ]);
        resetCreacion();
    };

    const handleRemoveTipoObra = (index: number) =>
        setSelectedTiposObra((prev) => prev.filter((_, i) => i !== index));

    const handleTipoChange = (event: SelectChangeEvent) => {
        const val = event.target.value;
        const asNumber = Number(val);
        setTipoSeleccionadoId(Number.isFinite(asNumber) ? asNumber : '');
        setSubtiposSeleccionadosIds([]);
    };

    useEffect(() => {
        const mapped = actividadesSeleccionadas.map((a) => {
            const esExcavacion = actividadesExcavacion.some((ae) => ae.id === a.id);

            if (esExcavacion) {
                return {
                    idActividad: a.id,
                    fechaInicio:
                        a.fechaInicio && a.fechaInicio.isValid()
                            ? a.fechaInicio.toISOString()
                            : null,
                    fechaFin: a.fechaFin && a.fechaFin.isValid() ? a.fechaFin.toISOString() : null
                };
            }

            return { idActividad: a.id };
        });

        const serialized = JSON.stringify(mapped);

        if (prevActividadesRef.current !== serialized) {
            prevActividadesRef.current = serialized;

            handleChange({
                target: {
                    name: 'avisosObraActividadesEspeciales',
                    value: mapped
                }
            });
        }
    }, [actividadesSeleccionadas, actividadesExcavacion, handleChange]);

    useEffect(() => {
        const mapped = selectedTiposObra.map((tipo) => ({
            idTipoObra: tipo.tipoId,
            idSubtiposObra: tipo.subtipos.map((sub) => sub.id)
        }));

        const serialized = JSON.stringify(mapped);

        if (prevTiposRef.current !== serialized) {
            prevTiposRef.current = serialized;

            handleChange({
                target: {
                    name: 'avisosObraTiposSubtipos',
                    value: mapped
                }
            });
        }
    }, [selectedTiposObra, handleChange]);

    const requiereDescripcionActividad = actividadesSeleccionadas.some((a) => a.id === 18);

    return (
        <>
            <SectionFormBox title={t('step2.section1.title')}>
                <Grid container spacing={2}>
                    {!isView && !addTipoObra && (
                        <Grid sx={{ mt: -8 }} item xs={12}>
                            <Button
                                variant="contained"
                                onClick={() => setAddTipoObra(true)}
                                sx={{ float: 'right' }}
                            >
                                {t('step2.actions.new')}
                            </Button>
                        </Grid>
                    )}

                    {addTipoObra && (
                        <Grid
                            item
                            xs={12}
                            sx={{ background: '#F8F8F8', borderRadius: 2, m: 4, p: 5 }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required>
                                        <InputLabel>{t('step2.section1.tipo')}</InputLabel>
                                        <Select
                                            value={
                                                tipoSeleccionadoId === ''
                                                    ? ''
                                                    : String(tipoSeleccionadoId)
                                            }
                                            onChange={handleTipoChange}
                                            label={t('step2.section1.tipo')}
                                        >
                                            {(tiposObra ?? []).map((option) => (
                                                <MenuItem key={option.id} value={String(option.id)}>
                                                    {option.nombre}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required>
                                        <InputLabel>{t('step2.section1.subtipo')}</InputLabel>
                                        <Select
                                            multiple
                                            value={subtiposSeleccionadosIds.map(String)}
                                            onChange={(e) => {
                                                const values = e.target.value as string[];
                                                setSubtiposSeleccionadosIds(
                                                    values.map((v) => Number(v))
                                                );
                                            }}
                                            label={t('step2.section1.subtipo')}
                                            disabled={!tipoSeleccionadoId}
                                        >
                                            {subtiposDelTipoSeleccionado.map((sub) => (
                                                <MenuItem key={sub.id} value={String(sub.id)}>
                                                    {sub.descripcion}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {!isView && (
                                    <Grid
                                        item
                                        xs={12}
                                        display="flex"
                                        justifyContent="flex-end"
                                        gap={2}
                                    >
                                        <Button
                                            variant="outlined"
                                            color="success"
                                            onClick={resetCreacion}
                                        >
                                            {t('step2.actions.cancel')}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            disabled={
                                                !tipoSeleccionadoId ||
                                                subtiposSeleccionadosIds.length === 0
                                            }
                                            onClick={handleAddTipoObra}
                                        >
                                            {t('step2.actions.add')}
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    )}

                    {selectedTiposObra.map((item, index) => (
                        <Grid
                            key={`${item.tipoId}-${index}`}
                            item
                            xs={12}
                            sx={{
                                backgroundColor: '#F3F8F8',
                                borderRadius: 2,
                                m: 1,
                                ml: 4,
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box>
                                <Typography fontWeight={600}>{item.tipoNombre}</Typography>
                                <Typography fontSize={14} color="text.secondary">
                                    {item.subtipos.map((s) => s.descripcion).join(', ')}
                                </Typography>
                            </Box>
                            {!isView && (
                                <IconButton
                                    onClick={() => handleRemoveTipoObra(index)}
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Grid>
                    ))}
                    {errors?.['tiposObra'] && (
                        <Grid item xs={12} sx={{ ml: 4 }}>
                            <Typography color="error" fontSize={14}>
                                {errors['tiposObra']}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </SectionFormBox>

            <SectionFormBox title={t('step2.section2.title')}>
                {loadingActividades && (
                    <Grid container justifyContent="center" alignItems="center">
                        <Box display="flex" py={4}>
                            <CircularProgress aria-label="Cargando actividades especiales..." />
                        </Box>
                    </Grid>
                )}
                <Grid container spacing={2}>
                    {actividadesExcavacion.map((actividad) => {
                        const isChecked = actividadesSeleccionadas.some(
                            (a) => a.id === actividad.id
                        );
                        const actividadSeleccionada = actividadesSeleccionadas.find(
                            (a) => a.id === actividad.id
                        );

                        return (
                            <Grid key={actividad.id} item xs={12} md={12} container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Box display="flex" alignItems="center">
                                        <Checkbox
                                            sx={{ m: 1 }}
                                            checked={isChecked}
                                            onChange={() => toggleActividad(actividad.id)}
                                            disabled={isView}
                                        />
                                        <Typography>
                                            {actividad.descripcion} (Código {actividad.codigo})
                                        </Typography>
                                    </Box>
                                </Grid>

                                {isChecked && (
                                    <>
                                        <Grid item xs={6} md={3}>
                                            <DatePicker
                                                label="Fecha de Inicio"
                                                value={actividadSeleccionada?.fechaInicio ?? null}
                                                disabled={isView}
                                                format="DD/MM/YYYY"
                                                onChange={(value) =>
                                                    setFechaActividad(
                                                        actividad.id,
                                                        'fechaInicio',
                                                        value
                                                    )
                                                }
                                                slotProps={{
                                                    textField: {
                                                        required: true,
                                                        fullWidth: true,
                                                        error: !!errors?.[
                                                            `actividad_${actividad.id}_inicio`
                                                        ],
                                                        helperText:
                                                            errors?.[
                                                                `actividad_${actividad.id}_inicio`
                                                            ]
                                                    }
                                                }}
                                                minDate={fechaInicioObra}
                                                maxDate={fechaFinObra}
                                            />
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <DatePicker
                                                label="Fecha de Fin"
                                                value={actividadSeleccionada?.fechaFin ?? null}
                                                format="DD/MM/YYYY"
                                                disabled={isView}
                                                onChange={(value) =>
                                                    setFechaActividad(
                                                        actividad.id,
                                                        'fechaFin',
                                                        value
                                                    )
                                                }
                                                slotProps={{
                                                    textField: {
                                                        required: true,
                                                        fullWidth: true,
                                                        error: !!errors?.[
                                                            `actividad_${actividad.id}_fin`
                                                        ],
                                                        helperText:
                                                            errors?.[
                                                                `actividad_${actividad.id}_fin`
                                                            ]
                                                    }
                                                }}
                                                minDate={
                                                    actividadSeleccionada?.fechaInicio &&
                                                    actividadSeleccionada.fechaInicio.isAfter(
                                                        fechaInicioObra
                                                    )
                                                        ? actividadSeleccionada.fechaInicio
                                                        : fechaInicioObra
                                                }
                                                maxDate={fechaFinObra}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        );
                    })}
                </Grid>
            </SectionFormBox>

            <SectionFormBox title={t('step2.section3.title')}>
                {loadingActividades && (
                    <Grid container justifyContent="center" alignItems="center">
                        <Box display="flex" py={4}>
                            <CircularProgress aria-label="Cargando actividades especiales..." />
                        </Box>
                    </Grid>
                )}
                <Grid container spacing={2}>
                    {actividadesGenerales.map((actividad) => {
                        const isChecked = actividadesSeleccionadas.some(
                            (a) => a.id === actividad.id
                        );

                        return (
                            <Grid item xs={12} sm={6} md={3} key={actividad.id}>
                                <Box display="flex" alignItems="center">
                                    <Checkbox
                                        checked={isChecked}
                                        onChange={() => toggleActividad(actividad.id)}
                                        disabled={isView}
                                    />
                                    <Typography>{actividad.descripcion}</Typography>
                                </Box>
                            </Grid>
                        );
                    })}

                    <Grid item xs={12}>
                        <TextField
                            name="descripcionActividad"
                            disabled={isView || !requiereDescripcionActividad}
                            multiline
                            fullWidth
                            required={requiereDescripcionActividad}
                            minRows={3}
                            label={t('step2.section3.placeholder')}
                            value={descripcionLocal}
                            onChange={(e) => {
                                const value = e.target.value;
                                setDescripcionLocal(value);
                                handleChange({
                                    target: {
                                        name: 'descripcionActividad',
                                        value
                                    }
                                });
                            }}
                            error={!!errors?.['descripcionActividad']}
                            helperText={errors?.['descripcionActividad']}
                        />
                    </Grid>
                </Grid>
            </SectionFormBox>
        </>
    );
};

export default StepTwo;
